import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  Users,
  CheckCircle2,
  Info,
  Loader2,
} from "lucide-react";
import { api } from "../../lib/api";
import PaymentTermSelect from "./PaymentTermSelect";

function useDebounce(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const PAGE_SIZE = 10;

const fullName = (u) => `${u?.first_name || ""} ${u?.last_name || ""}`.trim();

const initials = (u) => {
  const name = fullName(u);
  if (name) {
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0])
      .join("")
      .toUpperCase();
  }
  return (u?.email || "?")[0].toUpperCase();
};

function Avatar({ user, size = "md" }) {
  const sizes = size === "lg" ? "w-11 h-11 text-sm" : "w-9 h-9 text-xs";
  return (
    <div className={`${sizes} shrink-0 rounded-full bg-blue-50 text-blue-700 font-semibold flex items-center justify-center`}>
      {initials(user)}
    </div>
  );
}

function StepBadge({ n, done }) {
  return (
    <span
      className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${
        done ? "bg-green-100 text-green-700" : "bg-blue-600 text-white"
      }`}
    >
      {done ? <CheckCircle2 size={14} /> : n}
    </span>
  );
}

// Customer picker lists all registered users with pagination. Manual entry
// is not permitted (Admin doc business rule #2), so admin always picks from
// this list rather than typing a customer in.
export default function CreditLineIssue() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditLimit, setCreditLimit] = useState("");
  const [paymentTermDays, setPaymentTermDays] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = async (page = 1) => {
    setLoadingUsers(true);
    try {
      const res = await api.get("/admin/users", {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedQuery || undefined,
          exclude_issued_credit_line: true,
        },
      });
      setUsers(res.data?.data || []);
      setMeta(res.data?.meta || { page, limit: PAGE_SIZE, total: 0, totalPages: 1 });
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load customers");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (selectedUser) return;
    fetchUsers(1);
  }, [debouncedQuery, selectedUser]);

  const goPrev = () => {
    if (meta.page <= 1) return;
    fetchUsers(meta.page - 1);
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    fetchUsers(meta.page + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      toast.error("Select a registered customer from the list.");
      return;
    }
    if (!creditLimit || !paymentTermDays) {
      toast.error("Credit limit and payment term are required.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/admin/credit-lines", {
        user_id: selectedUser.user_id,
        credit_limit: Number(creditLimit),
        payment_term_days: Number(paymentTermDays),
      });
      toast.success("Credit line issued.");
      navigate("/admin/credit-lines");
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to issue credit line");
    } finally {
      setSubmitting(false);
    }
  };

  const rangeStart = meta.total === 0 ? 0 : (meta.page - 1) * (meta.limit || PAGE_SIZE) + 1;
  const rangeEnd = Math.min(meta.page * (meta.limit || PAGE_SIZE), meta.total);
  const termsDone = !!creditLimit && Number(creditLimit) > 0 && !!paymentTermDays;
  const canSubmit = !!selectedUser && termsDone && !submitting;

  return (
    <div className="p-4 sm:p-6 w-full">
      <button
        type="button"
        onClick={() => navigate("/admin/credit-lines")}
        className="text-sm text-gray-600 hover:text-gray-900 font-semibold flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={16} /> Back to Credit Lines
      </button>

      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">Issue Credit Line</h1>
        <p className="text-sm text-gray-500 mt-1">
          Pick a registered customer, then set their credit limit and payment term.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Step 1: customer */}
        <section className="lg:col-span-2 bg-white rounded-xl border">
          <div className="px-5 py-4 border-b flex items-center gap-3">
            <StepBadge n={1} done={!!selectedUser} />
            <div>
              <h2 className="font-semibold text-gray-800">Select Customer</h2>
              <p className="text-xs text-gray-500">Only customers without an existing credit line are listed.</p>
            </div>
          </div>

          <div className="p-5">
            {selectedUser ? (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-blue-200 bg-blue-50/50 rounded-lg p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar user={selectedUser} size="lg" />
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 break-words">
                      {fullName(selectedUser) || <span className="italic text-gray-500">Unnamed customer</span>}
                    </p>
                    <div className="flex flex-col sm:flex-row sm:gap-4 text-xs text-gray-600 mt-1">
                      {selectedUser.email && (
                        <span className="flex items-center gap-1 break-all">
                          <Mail size={12} className="shrink-0" /> {selectedUser.email}
                        </span>
                      )}
                      {selectedUser.mobile_no && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} className="shrink-0" /> {selectedUser.mobile_no}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedUser(null)}
                  className="rounded-lg border bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 self-start sm:self-auto"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, email or mobile"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="border rounded-lg pl-9 pr-9 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                <div className="border rounded-lg mt-3 divide-y">
                  <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide rounded-t-lg">
                    <span className="col-span-7">Customer</span>
                    <span className="col-span-3">Mobile</span>
                    <span className="col-span-2" />
                  </div>

                  {loadingUsers ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
                        <div className="w-9 h-9 rounded-full bg-gray-100" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-gray-100 rounded w-1/3" />
                          <div className="h-3 bg-gray-100 rounded w-1/2" />
                        </div>
                      </div>
                    ))
                  ) : users.length === 0 ? (
                    <div className="px-4 py-10 text-center">
                      <Users size={28} className="mx-auto text-gray-300 mb-2" />
                      <p className="text-sm font-semibold text-gray-700">No customers found</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {query
                          ? "Try a different name, email or mobile number."
                          : "Every registered customer already has a credit line."}
                      </p>
                    </div>
                  ) : (
                    users.map((u) => (
                      <button
                        type="button"
                        key={u.user_id}
                        onClick={() => setSelectedUser(u)}
                        className="w-full text-left grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-blue-50/60 transition-colors group"
                      >
                        <div className="col-span-9 sm:col-span-7 flex items-center gap-3 min-w-0">
                          <Avatar user={u} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {fullName(u) || <span className="italic text-gray-400">Unnamed customer</span>}
                            </p>
                            <p className="text-xs text-gray-500 truncate" title={u.email || ""}>
                              {u.email || "No email"}
                            </p>
                            <p className="text-xs text-gray-500 sm:hidden">{u.mobile_no || ""}</p>
                          </div>
                        </div>
                        <span className="hidden sm:block col-span-3 text-sm text-gray-700">
                          {u.mobile_no || <span className="text-gray-400">Not provided</span>}
                        </span>
                        <span className="col-span-3 sm:col-span-2 text-right">
                          <span className="inline-block rounded-lg border border-blue-200 text-blue-600 px-3 py-1 text-xs font-semibold group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-colors">
                            Select
                          </span>
                        </span>
                      </button>
                    ))
                  )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-3 text-xs text-gray-600">
                  <span>
                    Showing <span className="font-semibold">{rangeStart}</span> to{" "}
                    <span className="font-semibold">{rangeEnd}</span> of{" "}
                    <span className="font-semibold">{meta.total}</span> customers
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={meta.page <= 1 || loadingUsers}
                      onClick={goPrev}
                      className="flex items-center gap-1 border rounded-lg px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                      <ChevronLeft size={14} /> Prev
                    </button>
                    <span className="px-1">
                      Page {meta.page} of {meta.totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={meta.page >= meta.totalPages || loadingUsers}
                      onClick={goNext}
                      className="flex items-center gap-1 border rounded-lg px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                      Next <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </>
            )}

            <p className="flex items-start gap-1.5 text-xs text-gray-500 mt-4">
              <Info size={14} className="shrink-0 mt-px" />
              Only registered customers can be selected. Manual entry is not permitted.
            </p>
          </div>
        </section>

        {/* Step 2: terms */}
        <section className="bg-white rounded-xl border lg:sticky lg:top-6">
          <div className="px-5 py-4 border-b flex items-center gap-3">
            <StepBadge n={2} done={termsDone} />
            <div>
              <h2 className="font-semibold text-gray-800">Credit Terms</h2>
              <p className="text-xs text-gray-500">Limit and repayment window.</p>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Credit Limit</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">₹</span>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={creditLimit}
                  onChange={(e) => setCreditLimit(e.target.value)}
                  className="border rounded-lg pl-7 pr-14 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">INR</span>
              </div>
              {Number(creditLimit) > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  INR {Number(creditLimit).toLocaleString("en-IN")}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Payment Term</label>
              <PaymentTermSelect value={paymentTermDays} onChange={setPaymentTermDays} />
            </div>

            <div className="rounded-lg bg-gray-50 p-4 text-sm space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Summary</p>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Customer</span>
                <span className="font-medium text-gray-800 text-right break-all">
                  {selectedUser ? fullName(selectedUser) || selectedUser.email || selectedUser.mobile_no : "Not selected"}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Credit Limit</span>
                <span className="font-medium text-gray-800">
                  {Number(creditLimit) > 0 ? `INR ${Number(creditLimit).toLocaleString("en-IN")}` : "Not set"}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Payment Term</span>
                <span className="font-medium text-gray-800">
                  {paymentTermDays ? `${paymentTermDays} days` : "Not set"}
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full rounded-lg bg-blue-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Issuing…" : "Issue Credit Line"}
            </button>
            {!selectedUser && (
              <p className="text-xs text-gray-500 text-center">Select a customer to continue.</p>
            )}
          </div>
        </section>
      </form>
    </div>
  );
}
